/* eslint-disable react/jsx-key */
import { numberToPrettyString } from '@cian/utils/lib/shared/number_helpers';
import { isNil } from 'ramda';
import * as React from 'react';

import {
  IBuildingSchema,
  IBusinessShoppingCenterSchema,
  ILiftTypeSchema,
  IOfferSchema,
} from 'shared/types/api-models/offer-card/v1/get-offer-data';

import * as styles from './index.css';
import { isArrayWithItems } from '../../../utils';
import {
  getBuildingBuildYear,
  getBuildingDeveloper,
  getBuildingManagementCompany,
  getBuildingTotalArea,
  getTotalLiftsCount,
} from '../../../utils/building';
import { CurrencySymbol, getCurrencySymbol } from '../../../utils/formatters';
import {
  getBuildingType,
  getInputType,
  getLandArea,
  getLandStatusType,
  getStatusType,
} from '../../../utils/offer_helpers';
import { FOfferType, getOfferType } from '../../../utils/offer_type';
import { AdditionalFeaturesGroup } from '../../additional_features/group';
import { AdditionalFeatureItem } from '../../additional_features/item';
import {
  ConditionTypeNames,
  ParkingLocationTypeNames,
  ParkingPurposeTypeNames,
  ParkingTypeNames,
  FurniturePresenceNames,
} from '../../additional_features/locales';
import { locales } from '../locales';

export interface ICommercialAboutBuildingProps {
  building: IBuildingSchema | null;
  businessShoppingCenter: IBusinessShoppingCenterSchema | null;
  offer: IOfferSchema;
}

export class CommercialAboutBuilding extends React.PureComponent<ICommercialAboutBuildingProps, {}> {
  public render() {
    const { building, businessShoppingCenter: BSC, offer } = this.props;

    const offerType = getOfferType(offer);

    if (offerType !== FOfferType.Building) {
      return null;
    }

    let ceilingHeight: string | undefined;
    let travelatorCount: number | undefined;
    let escalatorCount: number | undefined;
    let buildingLiftCount: number | undefined;

    if (BSC) {
      if (BSC.travelatorCount) {
        travelatorCount = Number(BSC.travelatorCount);
      }

      if (BSC.escalatorCount) {
        escalatorCount = Number(BSC.escalatorCount);
      }

      if (BSC.ceilingHeight) {
        ceilingHeight = BSC.ceilingHeight;
      }
    }

    if (building) {
      if (building.ceilingHeight) {
        ceilingHeight = building.ceilingHeight;
      }

      if (!travelatorCount && isArrayWithItems<ILiftTypeSchema>(building.liftTypes)) {
        travelatorCount = building.liftTypes.reduce((acc, { type, count }) => {
          return type === 'travelator' ? acc + Number(count) : acc;
        }, 0);
      }

      if (!escalatorCount && isArrayWithItems<ILiftTypeSchema>(building.liftTypes)) {
        escalatorCount = building.liftTypes.reduce((acc, { type, count }) => {
          return type === 'escalator' ? acc + Number(count) : acc;
        }, 0);
      }

      if (!buildingLiftCount && isArrayWithItems<ILiftTypeSchema>(building.liftTypes)) {
        buildingLiftCount = building.liftTypes.reduce((acc, { type, count }) => {
          return type === 'lift' ? acc + Number(count) : acc;
        }, 0);
      }
    }

    const statusType = getStatusType(building, BSC);
    const landArea = getLandArea(offer, BSC);
    const landStatusType = getLandStatusType(offer, BSC);
    const inputType = getInputType(offer, BSC);
    const totalLiftsCount = BSC ? getTotalLiftsCount(building, BSC) : buildingLiftCount;
    const totalArea = getBuildingTotalArea(offer, building, BSC);
    const developer = getBuildingDeveloper(building, BSC);
    const managementCompany = getBuildingManagementCompany(building, BSC);
    const buildYear = getBuildingBuildYear(building, BSC);
    const buildingType = getBuildingType(building, BSC);

    return (
      <section className={styles['container']}>
        <h2 className={styles['title']}>О здании</h2>
        <AdditionalFeaturesGroup containerClassName={styles['article']} listClassName={styles['list']}>
          {Boolean(buildYear) && (
            <AdditionalFeatureItem className={styles['item']} name="Год постройки" value={Number(buildYear)} />
          )}
          {ceilingHeight && (
            <AdditionalFeatureItem
              className={styles['item']}
              name="Высота потолков"
              value={`${numberToPrettyString(Number(ceilingHeight))} м`}
            />
          )}
          {buildingType && (
            <AdditionalFeatureItem className={styles['item']} name="Возможное назначение" value={buildingType} />
          )}
          {offer.conditionType && (
            <AdditionalFeatureItem
              className={styles['item']}
              name="Состояние"
              value={ConditionTypeNames[offer.conditionType]}
            />
          )}
          {statusType && (
            <AdditionalFeatureItem className={styles['item']} name="Категория здания" value={statusType} />
          )}
          {(offer.furniturePresence || !isNil(offer.hasFurniture)) && (
            <AdditionalFeatureItem
              className={styles['item']}
              name="Мебель"
              value={
                (offer.furniturePresence && FurniturePresenceNames[offer.furniturePresence]) ||
                (offer.hasFurniture ? 'Есть' : 'Нет')
              }
            />
          )}
          {totalArea && <AdditionalFeatureItem className={styles['item']} name="Общая площадь" value={totalArea} />}
          {building && building.houseLineType && (
            <AdditionalFeatureItem
              className={styles['item']}
              name="Линия домов"
              value={locales.houseLineType[building.houseLineType]}
            />
          )}
        </AdditionalFeaturesGroup>
        <AdditionalFeaturesGroup containerClassName={styles['article']} listClassName={styles['list']}>
          {developer && <AdditionalFeatureItem className={styles['item']} name="Девелопер" value={developer} />}
          {managementCompany && (
            <AdditionalFeatureItem className={styles['item']} name="Управляющая компания" value={managementCompany} />
          )}
          {landArea && <AdditionalFeatureItem className={styles['item']} name="Площадь участка" value={landArea} />}
          {landStatusType && (
            <AdditionalFeatureItem className={styles['item']} name="Статус участка" value={landStatusType} />
          )}
        </AdditionalFeaturesGroup>
        <AdditionalFeaturesGroup containerClassName={styles['article']} listClassName={styles['list']}>
          {inputType && <AdditionalFeatureItem className={styles['item']} name="Вход" value={inputType} />}
          {Boolean(travelatorCount) && (
            <AdditionalFeatureItem className={styles['item']} name="Траволаторы" value={travelatorCount} />
          )}
          {Boolean(totalLiftsCount) && (
            <AdditionalFeatureItem className={styles['item']} name="Лифты" value={totalLiftsCount} />
          )}
          {Boolean(escalatorCount) && (
            <AdditionalFeatureItem className={styles['item']} name="Эскалаторы" value={escalatorCount} />
          )}
        </AdditionalFeaturesGroup>
        {building && building.parking && (
          <AdditionalFeaturesGroup containerClassName={styles['article']} listClassName={styles['list']}>
            {building.parking.type && (
              <AdditionalFeatureItem
                className={styles['item']}
                name="Парковка"
                value={ParkingTypeNames[building.parking.type]}
              />
            )}
            {building.parking.locationType && (
              <AdditionalFeatureItem
                className={styles['item']}
                name="Парковка"
                value={ParkingLocationTypeNames[building.parking.locationType]}
              />
            )}
            {building.parking.purposeType && (
              <AdditionalFeatureItem
                className={styles['item']}
                name="Тип парковки"
                value={ParkingPurposeTypeNames[building.parking.purposeType]}
              />
            )}
            {!isNil(building.parking.placesCount) && (
              <AdditionalFeatureItem
                className={styles['item']}
                name="Количество парковочных мест"
                value={building.parking.placesCount}
              />
            )}
            {building.parking.isFree ||
              (building.parking.currency && !isNil(building.parking.priceMonthly) && (
                <AdditionalFeatureItem
                  className={styles['item']}
                  name="Стоимость парковки"
                  value={
                    building.parking.isFree || building.parking.priceMonthly === 0
                      ? 'Бесплатно'
                      : `${numberToPrettyString(building.parking.priceMonthly)} ${getCurrencySymbol(
                          building.parking.currency,
                        )}/место`
                  }
                />
              ))}
            {!building.parking.isFree && building.parking.currency && !isNil(building.parking.priceEntry) && (
              <AdditionalFeatureItem
                className={styles['item']}
                name="Стоимость въезда"
                value={
                  building.parking.priceEntry === 0
                    ? 'Бесплатно'
                    : `${numberToPrettyString(building.parking.priceEntry)} ${getCurrencySymbol(
                        building.parking.currency,
                      )}`
                }
              />
            )}
          </AdditionalFeaturesGroup>
        )}
        {BSC &&
          BSC.parkings &&
          BSC.parkings.map(parking => (
            <AdditionalFeaturesGroup containerClassName={styles['article']} listClassName={styles['list']}>
              {parking.type && (
                <AdditionalFeatureItem
                  className={styles['item']}
                  name="Парковка"
                  value={ParkingTypeNames[parking.type]}
                />
              )}
              {!isNil(parking.placesCount) && (
                <AdditionalFeatureItem className={styles['item']} name="Количество мест" value={parking.placesCount} />
              )}
              {(parking.isFree || !isNil(parking.priceMonthly)) && (
                <AdditionalFeatureItem
                  className={styles['item']}
                  name="Стоимость"
                  value={parking.isFree ? 'Бесплатно' : `${parking.priceMonthly} ${CurrencySymbol.Rur}`}
                />
              )}
            </AdditionalFeaturesGroup>
          ))}
      </section>
    );
  }
}
